import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { siteContent } from "../content/siteContent";
import { useToast } from "../hooks/use-toast";

const ContactCTA = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    // Save locally for now (frontend-only)
    const existing = JSON.parse(localStorage.getItem("leads") || "[]");
    existing.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("leads", JSON.stringify(existing));
    toast({
      title: "Message sent!",
      description: `Thanks ${form.name || "there"}, we'll get back to you shortly.`,
    });
    setForm({ name: "", email: "", phone: "", message: "" });
  };

  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 to-cyan-50/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        {/* Left info */}
        <div>
          <div className="text-[#22b8db] font-semibold tracking-widest uppercase text-xs mb-3">
            Get In Touch
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#1a3a6e] mb-4 leading-tight">
            Leave us your info and we will get back to you.
          </h2>
          <p className="text-slate-500 leading-relaxed mb-8">
            Have a question about our services or want a customized quote? Our team
            is ready to help you build a healthier revenue cycle.
          </p>

          <div className="space-y-5">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-white shadow flex items-center justify-center text-[#22b8db]">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase text-slate-400 tracking-widest">Phone</div>
                <a href={siteContent.contact.phoneHref} className="text-[#1a3a6e] font-semibold">
                  {siteContent.contact.phone}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-white shadow flex items-center justify-center text-[#22b8db]">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase text-slate-400 tracking-widest">Email</div>
                <a href={`mailto:${siteContent.contact.email}`} className="text-[#1a3a6e] font-semibold">
                  {siteContent.contact.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-white shadow flex items-center justify-center text-[#22b8db]">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase text-slate-400 tracking-widest">Address</div>
                <div className="text-[#1a3a6e] font-semibold">
                  {siteContent.contact.addressLine1}, {siteContent.contact.addressLine2},{" "}
                  {siteContent.contact.addressLine3}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-white shadow flex items-center justify-center text-[#22b8db]">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase text-slate-400 tracking-widest">Working Hours</div>
                <div className="text-[#1a3a6e] font-semibold">{siteContent.contact.workingHours}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right form */}
        <form onSubmit={onSubmit} className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Name</label>
              <input
                required name="name" value={form.name} onChange={onChange}
                className="mt-1.5 w-full border border-slate-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#22b8db] focus:ring-2 focus:ring-[#22b8db]/20"
                placeholder="Your full name"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Phone</label>
              <input
                name="phone" value={form.phone} onChange={onChange}
                className="mt-1.5 w-full border border-slate-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#22b8db] focus:ring-2 focus:ring-[#22b8db]/20"
                placeholder="+1 555 000 0000"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Email</label>
            <input
              required type="email" name="email" value={form.email} onChange={onChange}
              className="mt-1.5 w-full border border-slate-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#22b8db] focus:ring-2 focus:ring-[#22b8db]/20"
              placeholder="you@company.com"
            />
          </div>
          <div className="mt-4">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Message</label>
            <textarea
              required name="message" value={form.message} onChange={onChange} rows={5}
              className="mt-1.5 w-full border border-slate-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#22b8db] focus:ring-2 focus:ring-[#22b8db]/20"
              placeholder="Tell us about your practice and what you need help with..."
            />
          </div>
          <button
            type="submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#22b8db] hover:bg-[#1ea3c2] text-white font-semibold py-3.5 rounded-md tracking-wide transition-colors shadow-md"
          >
            Submit <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </section>
  );
};

export default ContactCTA;
